<template>
  <div class="applicationSettingsList">
    <LoadingPlaceholder v-if="loading" />
    
    <template v-else>
      <Grid
        :ref="kgm_ref"
        :style="{height: 'auto'}"
        :data-items="kgm_computedGridItems(applicationSettings)"
        :columns="kgm_responsiveColumns()"
        :filterable="true"
        :filter="kgm_filter"
        :pageable="kgm_pagable"
        :page-size="kgm_take"
        :skip="kgm_skip"
        :take="kgm_take"
        :total="kgm_allGridItems(applicationSettings)"
        :sortable="{
          allowUnsort: kgm_allowUnsort,
          mode: kgm_sortMode
        }"
        :sort="kgm_sort"
        selected-field="selected"
        @rowclick="kgm_gridOnRowClick"
        @filterchange="kgm_gridFilterChange"
        @pagechange="kgm_gridPageChange"
        @sortchange="kgm_gridSortChange"
      >
        <div
          slot="keyFilter"
          slot-scope="{props, methods}"
          class="input-group"
        >
          <input
            ref="keyFilterInput"
            :value="props.value"
            type="text"
            class="form-control mr-2 rounded-right"
            placeholder="please enter a key"
            @input="(event) => {methods.change({operator: 'contains', field: props.field, value: event.target.value, syntheticEvent: event});}"
          >
          <div
            v-if="props.value"
            class="input-group-append"
          >
            <button
              class="btn btn-light border rounded"
              @click="kgm_resetInput('keyFilterInput', (event) => {methods.change({operator: '', field: '', value: '', syntheticEvent: event});})"
            >
              <font-awesome-icon
                class="gray"
                icon="times"
              />
            </button>
          </div>
        </div>
        <div
          slot="valueFilter"
          slot-scope="{props, methods}"
          class="input-group"
        >
          <input
            ref="valueFilterInput"
            :value="props.value"
            type="text"
            class="form-control mr-2 rounded-right"
            placeholder="please enter a value"
            @input="(event) => {methods.change({operator: 'contains', field: props.field, value: event.target.value, syntheticEvent: event});}"
          >
          <div
            v-if="props.value"
            class="input-group-append"
          >
            <button
              class="btn btn-light border rounded"
              @click="kgm_resetInput('valueFilterInput', (event) => {methods.change({operator: '', field: '', value: '', syntheticEvent: event});})"
            >
              <font-awesome-icon
                class="gray"
                icon="times"
              />
            </button>
          </div>
        </div>
        <template
          slot="optionsTemplate"
          slot-scope="{props}"
        >
          <td :class="props.className">
            <div class="btn-group">
              <button
                class="btn btn-primary btn-sm"
                @click="showSidebarEdit(props.dataItem)"
              >
                <font-awesome-icon
                  class="mr-2"
                  icon="edit"
                />
                <span>Edit</span>
              </button>
              <button
                class="btn btn-secondary btn-sm"
                @click="openConfirmModal(props.dataItem)"
              >
                <font-awesome-icon
                  class="mr-2 gray"
                  icon="trash"
                />
                <span>Remove</span>
              </button>
            </div>
          </td>
        </template>
      </Grid>
      <Sidebar
        v-if="showSidebar"
        :show-sidebar="showSidebar"
        @close="close"
      >
        <ApplicationSettingsAdd
          v-if="addingElement"
          @reload="reloadApplicationSettings"
        />
        <ApplicationSettingsEdit
          v-else
          :application-setting="applicationSetting"
          @reload="reloadApplicationSettings"
        />
      </Sidebar>
    </template>

    <SweetModal
      ref="confirmDelete"
      :title="$t('introOutroListComp.deleteElement')"
      icon="warning"
      blocking
      class="overflowHidden"
    >
      <p v-if="applicationSetting">
        {{ $t('sureToDelete', {'0' : applicationSetting.key}) }}
      </p>
      <button
        slot="button"
        class="btn btn-secondary float-left mb-3"
        @click="$refs.confirmDelete.close()"
      >
        <font-awesome-icon
          class="mr-2"
          icon="times"
        />{{ $t('cancel') }}
      </button>
      <button
        slot="button"
        class="btn btn-danger float-right mb-3"
        @click="removeApplicationSetting()"
      >
        <font-awesome-icon
          class="mr-2"
          icon="trash"
        />{{ $t('delete') }}
      </button>
      <div class="clearfix" />
    </SweetModal>
  </div>
</template>

<script>

import { SweetModal } from 'sweet-modal-vue';
import { kendoGridMixin } from '@/mixins/kendoGridMixin.js';
import Sidebar from '@/components/Base/Sidebar.vue';
import ApplicationSettingsAdd from '@/components/ApplicationSettings/ApplicationSettingsAdd';
import ApplicationSettingsEdit from '@/components/ApplicationSettings/ApplicationSettingsEdit';

export default {
  name: "ApplicationSettingsList",
  components: {
    SweetModal,
    Sidebar,
    ApplicationSettingsAdd,
    ApplicationSettingsEdit
  },
  mixins: [
    kendoGridMixin
  ],
  data () {
    return {
      loading: true,
      applicationSetting: null,
      applicationSettings: null,
      showSidebar: false,
      addingElement: false,
      currentId: null,
      deleteIndex: null,
      kgm_filter: {
        logic: "and",
        filters: [
          {
            field: 'key',
            operator: 'contains',
            value: ''
          }
        ]
      },
      kgm_sort: [
        {
          "field": "key",
          "dir": "asc"
        }
      ],
      kgm_columns: [
        {
          field: 'key',
          filterable: true,
          filter: 'text',
          title: 'Key',
          filterCell: 'keyFilter'
        },
        {
          field: 'value',
          filterable: true,
          filter: 'text',
          title: 'Value',
          filterCell: 'valueFilter'
        },
        {
          filterable: false,
          title: 'Options',
          cell: 'optionsTemplate',
        }
      ]
    }
  },
  metaInfo () {
    return {
      title: this.$t('menu.applicationSettings')
    }
  },
  created () {
    this.loadApplicationSettings();
  },
  methods: {
    //sidebar
    show () {
      this.showSidebar = true;
    },
    close () {
      this.showSidebar = false;
    },
    showSidebarAdd () {
      this.addingElement = true;
      this.show();
    },
    showSidebarEdit (emitValues) {
      this.setCurrentSetting(emitValues);
      this.addingElement = false;
      this.show();
    },
    setCurrentSetting (setting) {
      this.applicationSetting = Object.assign({}, setting); 
    },
    //end sidebar

    //api
    loadApplicationSettings () {
      this.loading = true;
      this.getApplicationSettings();
    },
    reloadApplicationSettings () {
      this.showSidebar = false;
      this.$emit("reloadAuditLogs");
      this.getApplicationSettings();
    },
    openConfirmModal (data) {
      this.setCurrentSetting(data);
      this.deleteIndex = data.id;
      this.$refs.confirmDelete.open();
    },
    removeApplicationSetting () {
      this.axios.delete(`/ApplicationSettings/DeleteApplicationSetting?id=${ this.deleteIndex }`)
      .then(() => {
        this.$snotify.success(this.$t('applicationSettingsComponent.deletedSuccessfully'));
        this.$emit("reloadAuditLogs");
        this.reloadApplicationSettings();
      })
      .finally(() => {
        this.deleteIndex = null;
        this.$refs.confirmDelete.close();
      });
    },
    getApplicationSettings () {
      this.axios.get('/ApplicationSettings/GetAllApplicationSettings')
      .then((response) => {
        if (response == null) {
          return;
        }
        if (response.data == null) {
          return;
        }
        this.applicationSettings = response.data;
      })
      .finally(() => {
        this.loading = false;
      });
    }
  }
}
</script>
<style>
.applicationSettingsList .k-grid table tr:hover td {
  background :rgb(219, 219, 219) !important;
  cursor: pointer !important;
}
</style>